<script setup lang="ts">
import { computed, inject, nextTick, onBeforeUnmount, onMounted, provide, ref, watch, type Ref } from 'vue'
import {
  resolveElements,
  type FlashcardElementInput,
  type FlashcardSize,
  type ThemeContext,
  type FlipAnimation,
  type ElementEventPayload,
} from './elements/registry'
import {
  FLASHCARD_AUTOFLIP,
  FLASHCARD_FORCE_DARK_MODE,
  FLASHCARD_INTERACTIVE,
  FLASHCARD_VISIBLE_FACE,
} from './context'

defineOptions({
  name: 'FlashcardBase',
})

const FLIP_ANIMATIONS: readonly FlipAnimation[] = ['rotate-y', 'rotate-x', 'slide', 'fade', 'none']

const props = withDefaults(
  defineProps<{
    frontElements?: FlashcardElementInput[]
    backElements?: FlashcardElementInput[]
    width?: number
    height?: number
    darkMode?: boolean
    backgroundColor?: string
    themeBackgroundColor?: string
    borderRadius?: number
    themeBorderRadius?: number
    borderColor?: string
    themeBorderColor?: string
    borderWidth?: number
    shadow?: string
    transitionDuration?: number
    themeTransitionDuration?: number
    flipAnimation?: FlipAnimation
    themeFlipAnimation?: FlipAnimation
    hoverFlip?: boolean
    showBackFirst?: boolean
    /** Visual style for checkbox-group checked marks. */
    checkboxTickStyle?: 'fill' | 'x' | 'haken'
    /** Disable click/hover interactions (used by the editor canvas). */
    interactive?: boolean
    /** Force-render a face without toggling state (used by the editor canvas). */
    forceFace?: 'front' | 'back'
    /** Allow elements to render outside the card face (used by the editor canvas). */
    allowOverflow?: boolean
    /** Include editor metadata data-attributes (editor-only). */
    editorMeta?: boolean
  }>(),
  {
    frontElements: () => [],
    backElements: () => [],
    width: 240,
    height: 160,
    darkMode: false,
    backgroundColor: '',
    themeBackgroundColor: '',
    borderRadius: 16,
    themeBorderRadius: undefined,
    borderColor: '',
    themeBorderColor: '',
    borderWidth: 0,
    shadow: '',
    transitionDuration: 400,
    themeTransitionDuration: undefined,
    flipAnimation: 'rotate-y',
    themeFlipAnimation: undefined,
    hoverFlip: false,
    showBackFirst: false,
    checkboxTickStyle: undefined,
    interactive: true,
    forceFace: undefined,
    allowOverflow: false,
    editorMeta: false,
  },
)

const checkboxTickStyleClass = computed(() => {
  const v = props.checkboxTickStyle
  if (v === 'x') return 'pfc-checkbox-tick--x'
  if (v === 'haken') return 'pfc-checkbox-tick--haken'
  return 'pfc-checkbox-tick--fill'
})

const emit = defineEmits<{
  (event: 'element-interaction', payload: ElementEventPayload): void
}>()

const cardSize = computed<FlashcardSize>(() => ({
  width: Math.max(1, props.width),
  height: Math.max(1, props.height),
}))

const forceDarkMode = inject(
  FLASHCARD_FORCE_DARK_MODE,
  computed(() => false),
)

const isDarkMode = computed(() => Boolean(props.darkMode) || forceDarkMode.value === true)

const DARK_DEFAULT = '#090a0f'
const DEFAULT_TRANSITION_MS = 400

const backgroundColor = computed(
  () =>
    props.themeBackgroundColor ||
    props.backgroundColor ||
    (isDarkMode.value ? DARK_DEFAULT : '#ffffff'),
)

const borderColor = computed(
  () => props.themeBorderColor || props.borderColor || (isDarkMode.value ? '#1e293b' : '#DBDBDB'),
)

const borderWidth = computed(() => {
  const requested = typeof props.borderWidth === 'number' && Number.isFinite(props.borderWidth)
  return requested ? Math.max(0, props.borderWidth) : 1
})

const normalizeAnimation = (value?: FlipAnimation) => {
  if (value && FLIP_ANIMATIONS.includes(value)) {
    return value
  }
  return undefined
}

const resolveTransitionDuration = (value?: number) => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return DEFAULT_TRANSITION_MS
  }
  return Math.max(0, value)
}

const transitionDuration = computed(() => {
  if (typeof props.themeTransitionDuration === 'number') {
    return resolveTransitionDuration(props.themeTransitionDuration)
  }
  return resolveTransitionDuration(props.transitionDuration)
})

const animationType = computed<FlipAnimation>(
  () =>
    normalizeAnimation(props.themeFlipAnimation) ??
    normalizeAnimation(props.flipAnimation) ??
    'rotate-y',
)

const resolveRadius = (requested?: number) => {
  if (typeof requested !== 'number' || Number.isNaN(requested)) {
    return 0
  }
  return Math.max(0, requested)
}

const baseRadius = computed(() => {
  if (typeof props.themeBorderRadius === 'number') {
    return resolveRadius(props.themeBorderRadius)
  }
  return resolveRadius(props.borderRadius)
})

const cardRadius = computed(() => {
  const limit = Math.min(cardSize.value.width, cardSize.value.height) / 2
  return Math.min(baseRadius.value, limit)
})

const animationClass = computed(() => `anim-${animationType.value}`)

const theme = computed<ThemeContext>(() => ({
  darkMode: isDarkMode.value,
  backgroundColor: backgroundColor.value,
  borderRadius: cardRadius.value,
  borderColor: borderColor.value,
  transitionDuration: transitionDuration.value,
  animation: animationType.value,
}))

const cardStyle = computed(() => ({
  width: `${cardSize.value.width}px`,
  height: `${cardSize.value.height}px`,
  background: 'transparent',
  color: isDarkMode.value ? '#f8fafc' : '#0f172a',
  borderRadius: `${cardRadius.value}px`,
  '--card-face-bg': backgroundColor.value,
  '--card-face-border': borderColor.value,
  '--card-face-border-width': `${borderWidth.value}px`,
  '--card-face-radius': `${cardRadius.value}px`,
  '--card-transition': `${transitionDuration.value}ms`,
  '--card-shadow': props.shadow || 'none',
}))

const emitElementEvent = (payload: ElementEventPayload) => {
  emit('element-interaction', payload)
}

const frontRenderable = computed(() =>
  resolveElements(props.frontElements, 'front', cardSize.value, theme.value, {
    emitElementEvent,
    includeEditorMeta: props.editorMeta,
  }),
)
const backRenderable = computed(() =>
  resolveElements(props.backElements, 'back', cardSize.value, theme.value, {
    emitElementEvent,
    includeEditorMeta: props.editorMeta,
  }),
)

const flipped = ref(Boolean(props.showBackFirst))
const suppressTransition = ref(true)

const isInteractive = computed(() => props.interactive !== false)

provide(FLASHCARD_INTERACTIVE, isInteractive)

const visibleFace = computed<'front' | 'back'>(() => (flipped.value ? 'back' : 'front'))
provide(FLASHCARD_VISIBLE_FACE, visibleFace)

const autoFlipEnabled = inject(
  FLASHCARD_AUTOFLIP,
  computed(() => false),
)

let autoFlipTimer: number | null = null

const clearAutoFlipTimer = () => {
  if (autoFlipTimer) {
    window.clearTimeout(autoFlipTimer)
    autoFlipTimer = null
  }
}

onBeforeUnmount(() => {
  clearAutoFlipTimer()
})

const syncForcedFace = () => {
  if (props.forceFace === 'back') {
    flipped.value = true
    return
  }
  if (props.forceFace === 'front') {
    flipped.value = false
  }
}

watch(
  () => props.forceFace,
  () => {
    syncForcedFace()
  },
  { immediate: true },
)

const showFront = () => {
  flipped.value = false
}

const showBack = () => {
  flipped.value = true
}

const resetToInitialFace = () => {
  if (props.showBackFirst) {
    showBack()
  } else {
    showFront()
  }
}

watch(
  () => props.showBackFirst,
  () => {
    resetToInitialFace()
  },
)

const toggle = () => {
  flipped.value = !flipped.value
}

const handleCardClick = () => {
  if (!isInteractive.value) return
  if (props.forceFace) return
  toggle()

  if (!autoFlipEnabled.value) return
  if (typeof window === 'undefined') return

  clearAutoFlipTimer()
  autoFlipTimer = window.setTimeout(() => {
    // Return to the initial face after a short delay.
    resetToInitialFace()
    autoFlipTimer = null
  }, 2200)
}

const handleMouseEnter = () => {
  if (!isInteractive.value || props.forceFace) {
    return
  }
  if (!props.hoverFlip) {
    return
  }
  if (props.showBackFirst) {
    showFront()
  } else {
    showBack()
  }
}

const handleMouseLeave = () => {
  if (!isInteractive.value || props.forceFace) {
    return
  }
  if (props.hoverFlip) {
    resetToInitialFace()
  }
}

defineExpose({
  toggle,
  showFront,
  showBack,
  reset: resetToInitialFace,
  flipped,
})

const cardRootRef = ref<HTMLElement | null>(null)

const isAssetLoading = ref(false)
let assetScanToken = 0

const hasImageElements = computed(() => {
  const all = [...(props.frontElements || []), ...(props.backElements || [])] as Array<
    Record<string, unknown>
  >
  return all.some((el) => el && typeof el === 'object' && el.type === 'image')
})

const scanAssetLoading = async () => {
  const token = (assetScanToken += 1)

  // If the card *contains* image elements, start hidden and show the shimmer immediately.
  // This prevents a flash of partially-loaded content.
  isAssetLoading.value = hasImageElements.value

  await nextTick()
  if (token !== assetScanToken) return

  const root = cardRootRef.value
  if (!root) return

  const imgs = Array.from(root.querySelectorAll('img')) as HTMLImageElement[]
  if (!imgs.length) {
    isAssetLoading.value = false
    return
  }

  let remaining = 0

  const done = () => {
    if (token !== assetScanToken) return
    remaining -= 1
    if (remaining > 0) return
    isAssetLoading.value = false
  }

  for (const img of imgs) {
    // Treat any already-complete state (success OR failure) as done.
    // If we only check naturalWidth > 0, already-failed images (naturalWidth = 0,
    // complete = true) would add event listeners that never fire → card stays blank.
    if (img.complete) continue
    remaining += 1

    const onDone = () => {
      img.removeEventListener('load', onDone)
      img.removeEventListener('error', onDone)
      done()
    }

    img.addEventListener('load', onDone)
    img.addEventListener('error', onDone)
  }

  if (remaining > 0) {
    isAssetLoading.value = true
  } else {
    isAssetLoading.value = false
  }
}

onMounted(() => {
  void scanAssetLoading()
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      suppressTransition.value = false
    })
  })
})

onBeforeUnmount(() => {
  assetScanToken += 1
})

watch(
  () => [
    props.frontElements,
    props.backElements,
    props.width,
    props.height,
    props.darkMode,
    props.backgroundColor,
    props.borderColor,
    props.borderRadius,
    props.borderWidth,
    props.shadow,
  ],
  () => {
    void scanAssetLoading()
  },
  { flush: 'post' },
)
</script>

<template>
  <div
    class="pfc-card"
    :class="[animationClass, checkboxTickStyleClass, { 'allow-overflow': props.allowOverflow, 'pfc-card--no-transition': suppressTransition }]"
    :style="cardStyle"
    :data-interactive="isInteractive ? 'true' : 'false'"
    :data-dark-mode="isDarkMode ? 'true' : 'false'"
    :data-loading="isAssetLoading ? 'true' : 'false'"
    ref="cardRootRef"
    @click="handleCardClick"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <div v-if="isAssetLoading" class="card-loader" aria-hidden="true" />

    <div class="card-opacity-wrapper">
      <div class="card-inner" :class="{ flipped }">
        <div class="face front">
          <component
            v-for="element in frontRenderable"
            :is="element.component"
            :key="element.key"
            v-bind="element.props"
          />

          <slot name="front-overlay" />
        </div>
        <div class="face back">
          <component
            v-for="element in backRenderable"
            :is="element.component"
            :key="element.key"
            v-bind="element.props"
          />

          <slot name="back-overlay" />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.pfc-card {
  perspective: 600px;
  cursor: pointer;
  touch-action: manipulation;
  user-select: none;
  position: relative;
}

.pfc-card[data-interactive='false'] {
  cursor: default;
}

.card-opacity-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
}

.pfc-card[data-loading='true'] .card-opacity-wrapper {
  opacity: 0;
}

.pfc-card[data-loading='false'] .card-opacity-wrapper {
  opacity: 1;
  transition: opacity 140ms ease;
}

.card-inner {
  position: relative;
  width: 100%;
  height: 100%;
  transition: transform var(--card-transition, 400ms) ease;
  transform-style: preserve-3d;
  border-radius: var(--card-face-radius, 0.5rem);
  box-shadow: var(--card-shadow, none);
}

.pfc-card--no-transition .card-inner,
.pfc-card--no-transition .face {
  transition: none !important;
}

.card-loader {
  position: absolute;
  inset: 0;
  z-index: 2;
  border-radius: var(--card-face-radius, 0.5rem);
  background: var(--card-face-bg, #ffffff);
  box-shadow:
    inset 0 0 0 var(--card-face-border-width, 0px) var(--card-face-border, #d4d4d4),
    var(--card-shadow, none);
  overflow: hidden;
}

.card-loader::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(
    110deg,
    rgba(148, 163, 184, 0.06) 20%,
    rgba(148, 163, 184, 0.22) 40%,
    rgba(148, 163, 184, 0.06) 60%
  );
  transform: translateX(-120%);
  animation: pfc-shimmer 1.15s ease-in-out infinite;
}

@keyframes pfc-shimmer {
  0% {
    transform: translateX(-120%);
  }

  100% {
    transform: translateX(120%);
  }
}

.card-inner.flipped {
  transform: none;
}

.face {
  position: absolute;
  inset: 0;
  border-radius: var(--card-face-radius, 0.5rem);
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  background: var(--card-face-bg, #ffffff);
  isolation: isolate;
  overflow: hidden;
}

.face::after {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  border-radius: inherit;
  box-shadow: inset 0 0 0 var(--card-face-border-width, 0px) var(--card-face-border, #d4d4d4);
  z-index: 2147483647;
}

.pfc-card.allow-overflow .face {
  overflow: hidden;
}

.pfc-card.allow-overflow .card-inner:not(.flipped) .face.front {
  overflow: visible;
}

.pfc-card.allow-overflow .card-inner.flipped .face.back {
  overflow: visible;
}

.face.back {
  transform: none;
}

.pfc-card.anim-rotate-y .card-inner.flipped {
  transform: rotateY(180deg);
}

.pfc-card.anim-rotate-y .face.back {
  transform: rotateY(180deg);
}

.pfc-card.anim-rotate-x .card-inner.flipped {
  transform: rotateX(180deg);
}

.pfc-card.anim-rotate-x .face.back {
  transform: rotateX(180deg);
}

.pfc-card.anim-slide .card-inner,
.pfc-card.anim-fade .card-inner,
.pfc-card.anim-none .card-inner {
  transform-style: flat;
}

.pfc-card.anim-slide .face,
.pfc-card.anim-fade .face,
.pfc-card.anim-none .face {
  backface-visibility: visible;
}

.pfc-card.anim-slide .face {
  transition:
    transform var(--card-transition, 400ms) ease,
    opacity var(--card-transition, 400ms) ease;
}

.pfc-card.anim-slide .face.front {
  transform: translateX(0%);
  opacity: 1;
}

.pfc-card.anim-slide .face.back {
  transform: translateX(100%);
  opacity: 0;
}

.pfc-card.anim-slide .card-inner.flipped .face.front {
  transform: translateX(-100%);
  opacity: 0;
}

.pfc-card.anim-slide .card-inner.flipped .face.back {
  transform: translateX(0%);
  opacity: 1;
}

.pfc-card.anim-slide .card-inner:not(.flipped) .face.front,
.pfc-card.anim-slide .card-inner.flipped .face.back {
  z-index: 2;
}

.pfc-card.anim-slide .card-inner:not(.flipped) .face.back {
  z-index: 1;
}

.pfc-card.anim-fade .face {
  transition: opacity var(--card-transition, 400ms) ease;
}

.pfc-card.anim-fade .face.front {
  opacity: 1;
  pointer-events: auto;
}

.pfc-card.anim-fade .face.back {
  opacity: 0;
  pointer-events: none;
}

.pfc-card.anim-fade .card-inner.flipped .face.front {
  opacity: 0;
  pointer-events: none;
}

.pfc-card.anim-fade .card-inner.flipped .face.back {
  opacity: 1;
  pointer-events: auto;
}

.pfc-card.anim-none .card-inner {
  transition: none;
  transform: none;
  transform-style: flat;
}

.pfc-card.anim-none .face {
  transition: none;
  transform: none;
  opacity: 0;
  pointer-events: none;
}

.pfc-card.anim-none .face.front {
  opacity: 1;
  pointer-events: auto;
}

.pfc-card.anim-none .card-inner.flipped .face.front {
  opacity: 0;
  pointer-events: none;
}

.pfc-card.anim-none .card-inner.flipped .face.back {
  opacity: 1;
  pointer-events: auto;
}
</style>
